import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Badge,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  Construction,
  Assignment,
  Person,
  Logout,
  Notifications,
  AdminPanelSettings,
  Announcement,
  MonetizationOn,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const getAvatarText = (fullName?: string | null, username?: string | null) => {
  const name = (fullName || '').trim();
  if (name) {
    const ch = name[0];
    return ch.toUpperCase ? ch.toUpperCase() : ch;
  }
  const user = (username || '').trim();
  if (user) {
    // 取第一個非數字字元，避免是 '0'（避免使用展開/迭代字串以符合較低 target）
    let firstNonDigit = '' as string;
    for (let i = 0; i < user.length; i++) {
      const c = user.charAt(i);
      if (!(c >= '0' && c <= '9')) { firstNonDigit = c; break; }
    }
    if (!firstNonDigit) firstNonDigit = user.charAt(0);
    return (firstNonDigit && firstNonDigit.toUpperCase) ? firstNonDigit.toUpperCase() : (firstNonDigit || 'U');
  }
  return 'U';
};

const drawerWidth = 240;

interface MenuItem {
  text: string;
  icon: React.ReactElement;
  path: string;
  requiredPermission?: number;
}

const Layout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    handleProfileMenuClose();
  };

  const menuItems: MenuItem[] = [
    { text: '儀表板', icon: <Dashboard />, path: '/dashboard' },
    { text: '車輛機具管理', icon: <Construction />, path: '/equipment' },
    { text: '租借訂單管理', icon: <Assignment />, path: '/rental' },
    {
      text: '費用明細',
      icon: <MonetizationOn />,
      path: '/fee-details',
      requiredPermission: 100,
    },
    { 
      text: '人員審核', 
      icon: <AdminPanelSettings />, 
      path: '/admin-review',
      requiredPermission: 100  // 需要權限 >= 100 才能看到
    },
    { 
      text: '人員管理', 
      icon: <Person />, 
      path: '/personnel-management',
      requiredPermission: 100  // 需要權限 >= 100 才能看到
    },
    { 
      text: '公告管理', 
      icon: <Announcement />, 
      path: '/announcements-manage',
      requiredPermission: 100  // 需要權限 >= 100 才能看到
    },
  ];

  const drawer = (
    <div>
      <Toolbar>
        <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 'bold' }}>
          機具租借系統
        </Typography>
      </Toolbar>
      <Divider />
      <List sx={{ pb: 8 }}> {/* 為底部公司名稱留出空間 */}
        {menuItems
          .filter((item) => {
            // 如果沒有 requiredPermission，則所有人都可以看到
            if (!item.requiredPermission) return true;
            // 如果有 requiredPermission，檢查用戶權限
            const userPermission = parseInt(user?.permission || '0', 10);
            return userPermission >= item.requiredPermission;
          })
          .map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => {
                navigate(item.path);
                setMobileOpen(false);
              }}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                  '& .MuiListItemIcon-root': {
                    color: 'white',
                  },
                },
              }}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      
      {/* 公司名稱 */}
      <Box sx={{ 
        position: 'absolute', 
        bottom: 0, 
        left: 0, 
        right: 0, 
        p: 2, 
        textAlign: 'center',
        borderTop: '1px solid rgba(255,255,255,0.1)',
        background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)'
      }}>
        <Typography variant="body2" sx={{ color: 'white', fontWeight: 'bold', mb: 0.5 }}>
          達客利科技有限公司
        </Typography>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.7rem' }}>
          DA-KE-LI TECHNOLOGY CO., LTD.
        </Typography>
      </Box>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            {location.pathname === '/admin-review' && (
              <AdminPanelSettings sx={{ mr: 1 }} />
            )}
            <Typography variant="h6" noWrap component="div">
              {location.pathname === '/admin-review' 
                ? '人員審核管理' 
                : menuItems.find(item => item.path === location.pathname)?.text || '機具租借系統'
              }
            </Typography>
          </Box>

          {/* 歡迎訊息 */}
          <Typography 
            variant="body1" 
            sx={{ 
              mr: 2, 
              display: { xs: 'none', md: 'block' },
              fontWeight: 'medium'
            }}
          >
            歡迎回來，{user?.name || user?.username}！
          </Typography>

          <IconButton color="inherit" sx={{ mr: 2 }}>
            <Badge badgeContent={0} color="error">
              <Notifications />
            </Badge>
          </IconButton>

          <IconButton
            size="large"
            edge="end"
            aria-label="account of current user"
            aria-controls="primary-search-account-menu"
            aria-haspopup="true"
            onClick={handleProfileMenuOpen}
            color="inherit"
          >
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
              {getAvatarText(user?.name as any, user?.username as any)}
            </Avatar>
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleProfileMenuClose}
            onClick={handleProfileMenuClose}
          >
            <MenuItem onClick={() => navigate('/profile')}>
              <ListItemIcon>
                <Person fontSize="small" />
              </ListItemIcon>
              <ListItemText>個人資料</ListItemText>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <Logout fontSize="small" />
              </ListItemIcon>
              <ListItemText>登出</ListItemText>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="mailbox folders"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          mt: 8,
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
};

export default Layout;
